/** A stretch of one line of a word diff. `changed` marks the words that differ
 * from the paired line; the text is the line's own text, never lower-cased. */
export interface DiffSegment {
  text: string;
  changed: boolean;
}

/** One line of a line diff: `same` in both texts, `removed` from the first,
 * `added` in the second. Only `diffText` sets `segments`, on both lines of a
 * removed/added pair it found alike; the segments concatenate to `text`. */
export interface DiffLine {
  type: "same" | "added" | "removed";
  text: string;
  segments?: readonly DiffSegment[];
}

export type TextDiff = { tooLarge: false; lines: DiffLine[] } | { tooLarge: true };

/** Past this many lines on either side the quadratic comparison is not worth
 * running; the caller falls back to showing both texts. */
export const MAX_DIFF_LINES = 2000;

/** The text as the diff reads it: `\n` line ends and no trailing newlines, so a
 * file that only differs by its final newline is not a different text. */
function normalize(text: string): string {
  return text.replace(/\r\n/g, "\n").replace(/\n+$/, "");
}

/** Whether two texts are the same as far as `diffLines` is concerned. */
export function sameText(before: string, after: string): boolean {
  return normalize(before) === normalize(after);
}

/** Whether two draft texts are the same as far as `diffText`, and so the shared
 * draft diff, is concerned: `sameText` over their Unicode-normalized (NFC) forms,
 * which `diffText` reads. The one rule that says "Sin cambios."; a surface that must
 * agree with the diff asks it here instead of restating it. */
export function sameDraftText(before: string, after: string): boolean {
  return sameText(before.normalize("NFC"), after.normalize("NFC"));
}

function splitLines(text: string): string[] {
  const normalized = normalize(text);
  return normalized === "" ? [] : normalized.split("\n");
}

/** The part of `a` and `b` that is left once the items they share at both ends
 * are set aside: `a[start..endA)` against `b[start..endB)`. */
function trimCommon(
  a: readonly string[],
  b: readonly string[],
): { start: number; endA: number; endB: number } {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  return { start, endA, endB };
}

/** Walks a longest-common-subsequence edit script of `a` into `b`, in order: a
 * `same` or `removed` step carries the index in `a`, an `added` step the index
 * in `b`. The table has `(a.length + 1) * (b.length + 1)` cells. */
function walkEdits(
  a: readonly string[],
  b: readonly string[],
  visit: (op: "same" | "removed" | "added", index: number) => void,
): void {
  const width = b.length + 1;
  // lcs[i * width + j]: length of the LCS of a[i..] and b[j..].
  const lcs = new Uint16Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        a[i] === b[j]
          ? (lcs[(i + 1) * width + j + 1] ?? 0) + 1
          : Math.max(lcs[(i + 1) * width + j] ?? 0, lcs[i * width + j + 1] ?? 0);
    }
  }

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      visit("same", i);
      i += 1;
      j += 1;
    } else if ((lcs[(i + 1) * width + j] ?? 0) >= (lcs[i * width + j + 1] ?? 0)) {
      visit("removed", i);
      i += 1;
    } else {
      visit("added", j);
      j += 1;
    }
  }
  for (; i < a.length; i += 1) visit("removed", i);
  for (; j < b.length; j += 1) visit("added", j);
}

/** A line-based longest-common-subsequence diff of `before` against `after`. A
 * changed line comes out as `removed` followed by `added`, in place. */
export function diffLines(before: string, after: string): TextDiff {
  const a = splitLines(before);
  const b = splitLines(after);
  if (a.length > MAX_DIFF_LINES || b.length > MAX_DIFF_LINES) return { tooLarge: true };

  // Lines shared at both ends need no table.
  const { start, endA, endB } = trimCommon(a, b);
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  const lines: DiffLine[] = a.slice(0, start).map((text) => ({ type: "same", text }));
  walkEdits(midA, midB, (op, index) => {
    lines.push({ type: op, text: (op === "added" ? midB[index] : midA[index]) as string });
  });
  for (const text of a.slice(endA)) lines.push({ type: "same", text });
  return { tooLarge: false, lines };
}

/** Tokens per side of a word diff, after the shared start and end are set aside.
 * Past it the lines are not word-diffed. */
export const MAX_WORD_TOKENS = 1_000;

/** Cell budget of the word work of ONE `diffText` call: each word LCS charges its
 * table, each similarity check the token count of the shorter line. */
export const MAX_WORD_DIFF_CELLS = 4_000_000;

/** The least `wordSimilarity` of two lines that are shown as a rewording rather
 * than as two unrelated lines. Inclusive. */
export const MIN_WORD_SIMILARITY = 0.5;

/** Removed lines times added lines of one run past which the run is not paired. */
export const MAX_RUN_PAIRS = 10_000;

/** When comparing every removed line of a run with every added one would not fit the
 * budget, only the pairs this many lines either side of the run's diagonal are compared. */
export const PAIR_BAND = 8;

/** A pictograph with its skin tone and emoji-style selector. */
const PICTOGRAPH = String.raw`\p{Extended_Pictographic}[\p{Emoji_Modifier}\p{Variation_Selector}]*`;
/** What glues the pictographs of one emoji together (a family, a person with a
 * profession). Spelled out here because an invisible character in a pattern is lost
 * to any editor. */
const ZERO_WIDTH_JOINER = String.fromCodePoint(0x200d);

/** An emoji (pictographs joined into one, or the two regional indicators of a flag),
 * a word (letters, digits, combining marks and underscores) or a single punctuation
 * mark. Spaces are not tokens. An emoji is one token so that a highlight never cuts
 * one in two, leaving a skin tone or an invisible joiner marked on its own. */
const TOKEN_PATTERN = new RegExp(
  [
    `${PICTOGRAPH}(?:${ZERO_WIDTH_JOINER}${PICTOGRAPH})*`,
    String.raw`\p{Regional_Indicator}{2}`,
    String.raw`[\p{L}\p{N}\p{M}_]+`,
    String.raw`[^\s\p{L}\p{N}\p{M}_]`,
  ].join("|"),
  "gu",
);

/** The tokens of `text`, normalized to NFC so a letter plus a combining mark is
 * the precomposed letter. */
export function tokenize(text: string): string[] {
  return text.normalize("NFC").match(TOKEN_PATTERN) ?? [];
}

/** A line read as tokens. `spans` hold each token as written and where it sits
 * in `text` (NFC), `lower` is what tokens are compared by, `counts` the multiset
 * of `lower`. */
interface Described {
  text: string;
  spans: readonly { text: string; start: number; end: number }[];
  lower: readonly string[];
  counts: ReadonlyMap<string, number>;
}

function describe(nfcText: string): Described {
  const spans: { text: string; start: number; end: number }[] = [];
  const lower: string[] = [];
  const counts = new Map<string, number>();
  for (const match of nfcText.matchAll(TOKEN_PATTERN)) {
    const start = match.index ?? 0;
    const token = match[0].toLowerCase();
    spans.push({ text: match[0], start, end: start + match[0].length });
    lower.push(token);
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  return { text: nfcText, spans, lower, counts };
}

function similarity(a: Described, b: Described): number {
  const total = a.lower.length + b.lower.length;
  if (a.lower.length === 0 || b.lower.length === 0) return 0;
  const [small, large] =
    a.counts.size <= b.counts.size ? [a.counts, b.counts] : [b.counts, a.counts];
  let shared = 0;
  for (const [token, count] of small) shared += Math.min(count, large.get(token) ?? 0);
  return (2 * shared) / total;
}

/** How alike two lines are, 0 to 1: `2 x shared / (tokens in both)` over the
 * multisets of their lower-cased tokens. Word order does not count; a line with
 * no token scores 0. */
export function wordSimilarity(before: string, after: string): number {
  return similarity(describe(before.normalize("NFC")), describe(after.normalize("NFC")));
}

/** The word work left in one `diffText` call. Once a step does not fit, no later
 * one runs either. */
interface WordBudget {
  remaining: number;
  exhausted: boolean;
}

function spend(budget: WordBudget | undefined, cells: number): boolean {
  if (budget === undefined) return true;
  if (budget.exhausted || cells > budget.remaining) {
    budget.exhausted = true;
    return false;
  }
  budget.remaining -= cells;
  return true;
}

/** Segments of `a.text` from a flag per token and per gap. Gap `k` is the text
 * before token `k`; the last gap is the text after the final token. */
function toSegments(
  a: Described,
  tokenFlags: readonly boolean[],
  gapFlags: readonly boolean[],
): DiffSegment[] {
  const segments: DiffSegment[] = [];
  const push = (text: string, changed: boolean) => {
    if (text === "") return;
    const last = segments[segments.length - 1];
    if (last !== undefined && last.changed === changed) last.text += text;
    else segments.push({ text, changed });
  };
  let cursor = 0;
  a.spans.forEach((span, k) => {
    push(a.text.slice(cursor, span.start), gapFlags[k] ?? false);
    push(span.text, tokenFlags[k] ?? false);
    cursor = span.end;
  });
  push(a.text.slice(cursor), gapFlags[a.spans.length] ?? false);
  return segments;
}

/** The text between the tokens of `a`, one more entry than tokens. */
function gapsOf(a: Described): string[] {
  const gaps: string[] = [];
  let cursor = 0;
  for (const span of a.spans) {
    gaps.push(a.text.slice(cursor, span.start));
    cursor = span.end;
  }
  gaps.push(a.text.slice(cursor));
  return gaps;
}

/** The word diff of two lines already read as tokens and known to be alike. */
function alignWords(
  a: Described,
  b: Described,
  budget: WordBudget | undefined,
): { before: DiffSegment[]; after: DiffSegment[] } | null {
  const { start, endA, endB } = trimCommon(a.lower, b.lower);
  if (endA - start > MAX_WORD_TOKENS || endB - start > MAX_WORD_TOKENS) return null;
  const midA = a.lower.slice(start, endA);
  const midB = b.lower.slice(start, endB);
  if (!spend(budget, (midA.length + 1) * (midB.length + 1))) return null;

  const changedA = a.lower.map(() => false);
  const changedB = b.lower.map(() => false);
  walkEdits(midA, midB, (op, index) => {
    if (op === "removed") changedA[start + index] = true;
    else if (op === "added") changedB[start + index] = true;
  });

  const gapsA = gapsOf(a);
  const gapsB = gapsOf(b);
  if (!changedA.includes(true) && !changedB.includes(true)) {
    // The lines differ only in letter case or in spacing: show those differences.
    const caseDiffers = a.spans.map((span, k) => span.text !== b.spans[k]?.text);
    const gapDiffers = gapsA.map((gap, k) => gap !== gapsB[k]);
    return {
      before: toSegments(a, caseDiffers, gapDiffers),
      after: toSegments(b, caseDiffers, gapDiffers),
    };
  }
  // A space between two changed words belongs to the highlight; any other does not.
  const between = (changed: readonly boolean[]) => [
    false,
    ...changed.slice(1).map((flag, k) => flag && (changed[k] ?? false)),
    false,
  ];
  return {
    before: toSegments(a, changedA, between(changedA)),
    after: toSegments(b, changedB, between(changedB)),
  };
}

/** The word-level diff of two lines: both sides as segments, the words that
 * differ marked `changed`. `null` when the lines are not alike enough
 * (`MIN_WORD_SIMILARITY`) or their differing middle is too long
 * (`MAX_WORD_TOKENS`); the caller then shows the whole lines. */
export function diffWords(
  before: string,
  after: string,
): { before: DiffSegment[]; after: DiffSegment[] } | null {
  const a = describe(before.normalize("NFC"));
  const b = describe(after.normalize("NFC"));
  if (similarity(a, b) < MIN_WORD_SIMILARITY) return null;
  return alignWords(a, b, undefined);
}

/** The runs of changed lines of `lines`, as the indexes of their removed and
 * added lines. */
function changeRuns(lines: readonly DiffLine[]): { removed: number[]; added: number[] }[] {
  const runs: { removed: number[]; added: number[] }[] = [];
  let run: { removed: number[]; added: number[] } | undefined;
  lines.forEach((line, index) => {
    if (line.type === "same") {
      run = undefined;
      return;
    }
    if (run === undefined) {
      run = { removed: [], added: [] };
      runs.push(run);
    }
    (line.type === "removed" ? run.removed : run.added).push(index);
  });
  return runs;
}

/** Pairs the removed and added lines of one run that are alike, without
 * crossing, for the greatest total similarity (ties go to the earlier lines).
 * When measuring every combination would not fit what is left of the budget, only
 * the combinations near the run's diagonal are measured (`PAIR_BAND`): a long run of
 * reworded paragraphs then keeps its highlights instead of losing all of them to the
 * cost of combinations that were never going to match. `null` when the run is too big
 * or the budget ran out while measuring it. */
function pairRun(
  removed: readonly number[],
  added: readonly number[],
  describeLine: (index: number) => Described,
  budget: WordBudget,
): [number, number][] | null {
  const rows = removed.length;
  const cols = added.length;
  if (rows === 0 || cols === 0 || rows * cols > MAX_RUN_PAIRS) return null;

  const left = removed.map(describeLine);
  const right = added.map(describeLine);
  let everyCombination = 0;
  for (const a of left) {
    for (const b of right) everyCombination += Math.min(a.lower.length, b.lower.length);
  }
  // The band runs from the diagonal of the first lines to that of the last ones (they
  // differ by the lines one side has more than the other) and PAIR_BAND beyond each.
  const first = Math.min(0, cols - rows) - PAIR_BAND;
  const last = Math.max(0, cols - rows) + PAIR_BAND;
  const banded = everyCombination > budget.remaining;

  // sim[r * cols + c] is the similarity of a candidate pair, 0 for any other.
  const sim = new Float64Array(rows * cols);
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      if (banded && (c - r < first || c - r > last)) continue;
      const cost = Math.min(
        (left[r] as Described).lower.length,
        (right[c] as Described).lower.length,
      );
      if (cost === 0) continue;
      if (!spend(budget, cost)) return null;
      const score = similarity(left[r] as Described, right[c] as Described);
      if (score >= MIN_WORD_SIMILARITY) sim[r * cols + c] = score;
    }
  }

  const width = cols + 1;
  // best[r * width + c]: the greatest total over removed[r..] and added[c..].
  const best = new Float64Array((rows + 1) * width);
  for (let r = rows - 1; r >= 0; r -= 1) {
    for (let c = cols - 1; c >= 0; c -= 1) {
      const skip = Math.max(best[(r + 1) * width + c] ?? 0, best[r * width + c + 1] ?? 0);
      const score = sim[r * cols + c] ?? 0;
      best[r * width + c] =
        score > 0 ? Math.max(skip, score + (best[(r + 1) * width + c + 1] ?? 0)) : skip;
    }
  }

  const pairs: [number, number][] = [];
  let r = 0;
  let c = 0;
  while (r < rows && c < cols) {
    const score = sim[r * cols + c] ?? 0;
    if (score > 0 && score + (best[(r + 1) * width + c + 1] ?? 0) >= (best[r * width + c] ?? 0)) {
      pairs.push([removed[r] as number, added[c] as number]);
      r += 1;
      c += 1;
    } else if ((best[(r + 1) * width + c] ?? 0) >= (best[r * width + c + 1] ?? 0)) {
      r += 1;
    } else {
      c += 1;
    }
  }
  return pairs;
}

/** `diffLines` over the NFC form of both texts, with a word diff on every
 * removed line that is paired with an added line it is alike (see
 * `MIN_WORD_SIMILARITY`). Pairing never reorders rows. Past any limit (lines,
 * tokens, pairs, cell budget) the diff degrades to line marks; nothing throws. */
export function diffText(before: string, after: string): TextDiff {
  const result = diffLines(before.normalize("NFC"), after.normalize("NFC"));
  if (result.tooLarge) return result;
  const lines = result.lines;

  const described = new Map<number, Described>();
  const describeLine = (index: number): Described => {
    let known = described.get(index);
    if (known === undefined) {
      known = describe((lines[index] as DiffLine).text);
      described.set(index, known);
    }
    return known;
  };

  const budget: WordBudget = { remaining: MAX_WORD_DIFF_CELLS, exhausted: false };
  for (const run of changeRuns(lines)) {
    if (budget.exhausted) break;
    const pairs = pairRun(run.removed, run.added, describeLine, budget);
    if (pairs === null) continue;
    for (const [oldIndex, newIndex] of pairs) {
      const words = alignWords(describeLine(oldIndex), describeLine(newIndex), budget);
      if (budget.exhausted) break;
      if (words === null) continue;
      lines[oldIndex] = { ...(lines[oldIndex] as DiffLine), segments: words.before };
      lines[newIndex] = { ...(lines[newIndex] as DiffLine), segments: words.after };
    }
  }
  return { tooLarge: false, lines };
}

/** A row of a folded diff: a line to show, or the unchanged lines hidden behind
 * a fold. */
export type DiffRow =
  | { kind: "line"; line: DiffLine }
  | { kind: "fold"; hidden: readonly DiffLine[] };

/** Unchanged lines kept in view beside each change. */
const FOLD_CONTEXT = 3;
/** Fewest unchanged lines worth hiding behind a fold. */
const FOLD_MIN_HIDDEN = 3;

/** The rows of `lines` with each run of unchanged lines folded when hiding it
 * keeps `FOLD_CONTEXT` lines beside every change and hides at least
 * `FOLD_MIN_HIDDEN`: a leading or trailing run folds from 6 lines, a middle one
 * from 9. A diff with no change is returned unfolded. */
export function foldUnchanged(lines: readonly DiffLine[]): DiffRow[] {
  const asRows = (values: readonly DiffLine[]): DiffRow[] =>
    values.map((line) => ({ kind: "line", line }));
  if (lines.every((line) => line.type === "same")) return asRows(lines);

  const rows: DiffRow[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] as DiffLine;
    if (line.type !== "same") {
      rows.push({ kind: "line", line });
      index += 1;
      continue;
    }
    let end = index;
    while (end < lines.length && (lines[end] as DiffLine).type === "same") end += 1;
    const run = lines.slice(index, end);
    const before = index > 0 ? FOLD_CONTEXT : 0;
    const after = end < lines.length ? FOLD_CONTEXT : 0;
    if (run.length - before - after >= FOLD_MIN_HIDDEN) {
      rows.push(...asRows(run.slice(0, before)));
      rows.push({ kind: "fold", hidden: run.slice(before, run.length - after) });
      rows.push(...asRows(run.slice(run.length - after)));
    } else {
      rows.push(...asRows(run));
    }
    index = end;
  }
  return rows;
}
